import { useTranslation } from '@/hooks/use-translation';
import { $apiAllo } from '@/services/api-allo/client';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

// TODO: Remove once ContactView is added to the OpenAPI spec
export type ContactView = {
  id: string;
  name: string;
  filter: {
    and: Array<{
      field: string;
      operator: string;
      value?: string;
    }>;
  };
  sort?: Array<{
    field: string;
    order: string;
  }> | null;
  owner_type?: 'USER' | 'TEAM';
  is_default?: boolean;
  created_at?: string;
  updated_at?: string;
};

// Cast to bypass missing OpenAPI spec paths - remove once spec is updated
const api = $apiAllo as any;

/**
 * Hook to fetch all contact views for the current user/team
 */
export function useContactViewsQuery() {
  return api.useQuery('get', '/v1/contact/view', {});
}

/**
 * Hook to fetch a single contact view by ID
 */
export function useContactViewQuery(viewId: string | null | undefined) {
  return api.useQuery(
    'get',
    '/v1/contact/view/{viewId}',
    {
      params: {
        path: { viewId: viewId ?? '' },
      },
    },
    {
      enabled: !!viewId,
    },
  );
}

interface UseCreateContactViewMutationOptions {
  onSuccess?: (view: ContactView) => void;
  onError?: () => void;
}

/**
 * Hook to create a new contact view
 */
export function useCreateContactViewMutation({
  onSuccess,
  onError,
}: UseCreateContactViewMutationOptions = {}) {
  const { t } = useTranslation('desktop_ui');
  const queryClient = useQueryClient();
  const viewsQueryKey = api.queryOptions('get', '/v1/contact/view', {}).queryKey;

  return api.useMutation('post', '/v1/contact/view', {
    onSuccess: async (response: { data: unknown }) => {
      const newView = response.data as ContactView;

      // Immediately add the new view to the cache using the correct query key
      queryClient.setQueryData<{ data: { views: ContactView[] } }>(
        viewsQueryKey,
        (oldData: any) => {
          if (!oldData?.data?.views) {
            return oldData;
          }
          return {
            ...oldData,
            data: {
              ...oldData.data,
              views: [...oldData.data.views, newView],
            },
          };
        },
      );

      await queryClient.invalidateQueries({ queryKey: viewsQueryKey });

      toast.success(t('components.contacts.views.createSuccess'), {
        position: 'top-center',
      });

      onSuccess?.(newView);
    },
    onError: () => {
      toast.error(t('components.contacts.views.createError'), {
        position: 'top-center',
      });
      onError?.();
    },
  });
}

interface UseUpdateContactViewMutationOptions {
  onSuccess?: (view: ContactView) => void;
  onError?: () => void;
}

/**
 * Hook to update an existing contact view
 */
export function useUpdateContactViewMutation({
  onSuccess,
  onError,
}: UseUpdateContactViewMutationOptions = {}) {
  const { t } = useTranslation('desktop_ui');
  const queryClient = useQueryClient();
  const viewsQueryKey = api.queryOptions('get', '/v1/contact/view', {}).queryKey;

  return api.useMutation('patch', '/v1/contact/view/{viewId}', {
    onSuccess: async (response: { data: unknown }) => {
      const updatedView = response.data as ContactView;

      // Immediately update the cache with the updated view using the correct query key
      queryClient.setQueryData<{ data: { views: ContactView[] } }>(
        viewsQueryKey,
        (oldData: any) => {
          if (!oldData?.data?.views) {
            return oldData;
          }
          return {
            ...oldData,
            data: {
              ...oldData.data,
              views: oldData.data.views.map((v: ContactView) =>
                v.id === updatedView.id ? updatedView : v,
              ),
            },
          };
        },
      );

      // Also invalidate to ensure we stay in sync with the server
      await queryClient.invalidateQueries({ queryKey: viewsQueryKey });

      toast.success(t('components.contacts.views.updateSuccess'), {
        position: 'top-center',
      });

      onSuccess?.(updatedView);
    },
    onError: () => {
      toast.error(t('components.contacts.views.updateError'), {
        position: 'top-center',
      });
      onError?.();
    },
  });
}

interface UseDeleteContactViewMutationOptions {
  onSuccess?: () => void;
  onError?: () => void;
}

/**
 * Hook to delete a contact view
 */
export function useDeleteContactViewMutation({
  onSuccess,
  onError,
}: UseDeleteContactViewMutationOptions = {}) {
  const { t } = useTranslation('desktop_ui');
  const queryClient = useQueryClient();
  const viewsQueryKey = api.queryOptions('get', '/v1/contact/view', {}).queryKey;

  return api.useMutation('delete', '/v1/contact/view/{viewId}', {
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: viewsQueryKey });

      toast.success(t('components.contacts.views.deleteSuccess'), {
        position: 'top-center',
      });

      onSuccess?.();
    },
    onError: () => {
      toast.error(t('components.contacts.views.deleteError'), {
        position: 'top-center',
      });
      onError?.();
    },
  });
}

interface UseDuplicateContactViewMutationOptions {
  onSuccess?: (view: ContactView) => void;
  onError?: () => void;
}

/**
 * Hook to duplicate a contact view
 */
export function useDuplicateContactViewMutation({
  onSuccess,
  onError,
}: UseDuplicateContactViewMutationOptions = {}) {
  const { t } = useTranslation('desktop_ui');
  const queryClient = useQueryClient();
  const viewsQueryKey = api.queryOptions('get', '/v1/contact/view', {}).queryKey;

  return api.useMutation('post', '/v1/contact/view/{viewId}/duplicate', {
    onSuccess: async (response: { data: unknown }) => {
      const newView = response.data as ContactView;

      // Immediately add the new view to the cache using the correct query key
      queryClient.setQueryData<{ data: { views: ContactView[] } }>(
        viewsQueryKey,
        (oldData: any) => {
          if (!oldData?.data?.views) {
            return oldData;
          }
          return {
            ...oldData,
            data: {
              ...oldData.data,
              views: [...oldData.data.views, newView],
            },
          };
        },
      );

      await queryClient.invalidateQueries({ queryKey: viewsQueryKey });

      toast.success(t('components.contacts.views.duplicateSuccess'), {
        position: 'top-center',
      });

      onSuccess?.(newView);
    },
    onError: () => {
      toast.error(t('components.contacts.views.duplicateError'), {
        position: 'top-center',
      });
      onError?.();
    },
  });
}
